import { useMemo, type ComponentProps } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { IbmLogo } from '@mint-lib/icons';
import { useFlags } from '@mint-lib/routing-context';
import { typographyVariants } from '@mint-lib/tw-components';
import { AvatarAi } from '@mint-lib/tw-components/ai';
import { cn } from '@mint-lib/tw-utils';

import { useAgents } from '../../../hooks/useAgents.jsx';
import { AgentType } from '../../../types/agents.js';

type Props = {
  agentName: string;
  avatarSize?: ComponentProps<typeof AvatarAi>['size'];
  className?: string;
  labelDirection?: 'row' | 'column';
  accordionView?: boolean;
  typographyClassName?: string;
};

const AgentLabel = ({
  agentName,
  accordionView,
  avatarSize,
  className,
  labelDirection = 'row',
  typographyClassName,
}: Props) => {
  const ff = useFlags('@mint/arm', ['poweredByLabel']);
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { agents } = useAgents();

  const aiAgent = useMemo((): AgentType => {
    return (
      agents.find((agent) => agent.agentName === agentName) || agents[0] // AI Assistant
    );
  }, [agentName, agents]);

  return (
    <div
      className={cn(
        'flex flex-row items-center gap-2',
        accordionView ? 'h-full w-full' : 'w-auto',
        className,
      )}
      data-testid="agent-label"
    >
      <AvatarAi
        role={aiAgent.role}
        size={avatarSize}
        hasPointer={accordionView}
      />
      <div
        className={cn(
          'flex items-center',
          labelDirection === 'row' ? 'flex-row gap-2' : 'flex-col gap-0.5',
          accordionView ? 'w-full justify-between' : 'w-auto',
        )}
      >
        <p
          className={
            typographyClassName ??
            typographyVariants({
              variant: 'bodyShort02',
              color: 'secondary',
            })
          }
        >
          {aiAgent.label}
        </p>
        {ff.poweredByLabel.enabled && aiAgent.meta?.hasPoweredLabel && (
          <div className="flex flex-row items-center gap-0.5">
            <p
              className={cn(
                typographyVariants({
                  variant: 'caption2',
                  color: 'primary',
                }),
                'whitespace-nowrap',
              )}
            >
              {t('Powered by')}
            </p>
            <IbmLogo className="size-5 fill-icon-primary" />
          </div>
        )}
      </div>
    </div>
  );
};

export { AgentLabel };
